import type { FC, ReactElement } from 'react';
import { useEffect, useState } from 'react';

import { useAudioTrigger } from '../../../../controllers/audioController/hooks';
import type { ChipValueType } from '../../../../store/slices/chips/types';
import { useModifiers } from '../../../../styles/hooks/useModifiers';
import { Tooltip, TooltipPositions } from '../../../tooltip/Tooltip';
import { ChipConnected } from '../../chip/ChipConnected';
import { ChipStackButton } from '../chipStackButton';
import type { ChipStackProps } from '../types';
import { useSiblingChips } from '../useSiblingChip';

import styles from './clipChipStack.module.css';

export const ClipChipStack: FC<ChipStackProps> = ({
  chips,
  disabled,
  nextChip,
  onChipSelect,
  previousChip,
  selectedChip,
  stacked,
  isChipStackDrawerClosed,
}): ReactElement | null => {
  const [open, setOpen] = useState(false);
  const [prePreviousChip] = useSiblingChips(chips, previousChip); // To get previous chip of the previous chip for vertical clip chip stack

  const audioTrigger = useAudioTrigger();
  const handleToggle = (): void => {
    if (!disabled) {
      setOpen((prev) => !prev);
      audioTrigger.play({ name: 'chipClick' });
    }
  };

  const close = (): void => {
    setOpen(false);
  };

  useEffect(() => {
    if (disabled || isChipStackDrawerClosed) {
      close();
    }
  }, [disabled, isChipStackDrawerClosed]);

  const mods = useModifiers(styles, 'clipChipStack', { disabled, open });
  const triggerMods = useModifiers(styles, 'clipChipStack__trigger', {
    disabled,
    vertical: stacked === 'vertical',
    horizontal: stacked === 'horizontal',
  });

  const tooltipDisabled = !disabled;

  const getChipStackTrigger = (
    firstChip: ChipValueType | null,
    middleChip: ChipValueType | null,
    lastChip: ChipValueType | null,
    triggerChipMods: boolean
  ): ReactElement => {
    return (
      <button className={`${styles.clipChipStack__trigger} ${triggerMods}`} onClick={handleToggle} type="button">
        <span className={`${styles.clipChipStack__triggerContainer}`}>
          {firstChip ? (
            <span className={`${styles.clipChipStack__ear} ${triggerChipMods ? triggerMods : ''}`}>
              <ChipConnected size="inherit" value={firstChip.value} weight={firstChip.weight} />
            </span>
          ) : null}
          {middleChip ? (
            <span className={`${styles.clipChipStack__chip} ${triggerChipMods ? triggerMods : ''}`}>
              <ChipConnected size="inherit" value={middleChip.value} weight={middleChip.weight} />
            </span>
          ) : null}
          {lastChip ? (
            <span className={`${styles.clipChipStack__ear} ${triggerChipMods ? triggerMods : ''}`}>
              <ChipConnected size="inherit" value={lastChip.value} weight={lastChip.weight} />
            </span>
          ) : null}
        </span>
      </button>
    );
  }

  const getChipStack = (): ReactElement => {
    if (stacked) {
      return getChipStackTrigger(previousChip, selectedChip, prePreviousChip, true);
    } else {
      return getChipStackTrigger(previousChip, selectedChip, nextChip, false);
    }
  }

  if (!selectedChip) {
    return null;
  }

  return (
    <div data-testid="chip-stack-clip" className={`${styles.clipChipStack} ${mods}`}>
      <Tooltip text="INSUFFICIENTFUNDS" disabled={tooltipDisabled} displayOnTouch position={TooltipPositions.Bottom}>
        {getChipStack()}
        <div className={styles.clipChipStack__drawerContainer}>
          <div className={styles.clipChipStack__drawerBackground} onClick={close}></div>
          <div className={styles.clipChipStack__drawer}>
            {chips.map((chip) => (
              <Tooltip
                text="INSUFFICIENTFUNDS"
                disabled={!chip.disabled || chip.toolTipDisabled}
                displayOnTouch
                key={chip.weight}
              >
                <ChipStackButton
                  active={selectedChip.weight === chip.weight}
                  chip={chip}
                  disabled={chip.disabled}
                  onChipSelect={onChipSelect}
                  onClick={close}
                  small
                />
              </Tooltip>
            ))}
          </div>
        </div>
      </Tooltip>
    </div>
  );
};
